/**
 * テスト用の広告・課金。**お金は動かない。**
 *
 * staging や開発サーバーで `?platform=mock` を付けると有効になる。
 * アプリにする前に、コンティニュー・おはし・買い切りの流れをブラウザで確かめるためのもの。
 */

import { notifyAdUnavailable } from './adLifecycle';
import type { Platform, PurchaseOutcome, RewardedPlacement, TrackingResult } from './Platform';
import { PRODUCTS, type ProductId } from './products';

const PLACEMENT_LABEL: Record<RewardedPlacement, string> = {
  continue: 'コンティニュー',
  item: 'おはし1本',
  rest: 'おやすみ券（連続日数を守る）',
  rescue: 'おはし1本',
};

function dialog(options: {
  title: string;
  body: string;
  buttons: Array<{ label: string; value: string; primary?: boolean }>;
  autoCloseMs?: number;
  autoValue?: string;
}): Promise<string> {
  return new Promise((resolve) => {
    const root = document.createElement('div');
    root.className = 'mock-ad';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    const card = document.createElement('div');
    card.className = 'mock-ad__card';
    const title = document.createElement('p');
    title.className = 'mock-ad__title';
    title.textContent = options.title;
    const body = document.createElement('p');
    body.className = 'mock-ad__body';
    body.textContent = options.body;
    const actions = document.createElement('div');
    actions.className = 'mock-ad__actions';
    card.append(title, body, actions);
    root.append(card);

    let timer: number | undefined;
    const finish = (value: string) => {
      if (timer !== undefined) window.clearTimeout(timer);
      root.remove();
      resolve(value);
    };
    options.buttons.forEach((b) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = b.primary ? 'btn btn--primary' : 'btn';
      btn.textContent = b.label;
      btn.addEventListener('click', () => finish(b.value));
      actions.append(btn);
    });
    if (options.autoCloseMs && options.autoValue) {
      timer = window.setTimeout(() => finish(options.autoValue!), options.autoCloseMs);
    }
    document.body.append(root);
  });
}

export class MockPlatform implements Platform {
  readonly kind = 'mock' as const;
  readonly monetized = true;
  readonly canPurchase: boolean;

  /** 値段の問い合わせを、あと何回失敗させるか（起動時に電波が悪かった、を試す）。 */
  private priceFailures: number;
  private pricesReady: boolean;
  private readonly priceDelayMs: number;

  /**
   * - `purchases: false` で Android 版（広告だけ）の見え方を試せる（`?platform=mock-android`）
   * - `priceFailures: n` で、値段が取れない状態から始める（`?mockprice=late`。ショップを開くと取り直す）
   */
  constructor(options: { purchases?: boolean; priceFailures?: number; priceDelayMs?: number } = {}) {
    this.canPurchase = options.purchases ?? true;
    this.priceFailures = Math.max(0, options.priceFailures ?? 0);
    this.pricesReady = this.priceFailures === 0;
    this.priceDelayMs = options.priceDelayMs ?? 600;
    // 起動時の1回目の問い合わせ（失敗させる設定なら、ここで1回分使う）
    if (!this.pricesReady) this.priceFailures -= 1;
  }

  async init(): Promise<void> {}

  /** ブラウザには ATT が無いので何もしない（実機の TestFlight で確かめる）。 */
  async askTrackingPermission(): Promise<TrackingResult | null> {
    return null;
  }

  async ownedProducts(): Promise<ProductId[]> {
    return [];
  }

  async showRewarded(placement: RewardedPlacement): Promise<boolean> {
    const value = await dialog({
      title: 'テスト広告（リワード）',
      body: `本番ではここに動画広告が流れます。見終わると「${PLACEMENT_LABEL[placement]}」がもらえます。`,
      buttons: [
        { label: '見終わった', value: 'ok', primary: true },
        { label: '途中で閉じる', value: 'cancel' },
        { label: '読み込めなかった', value: 'unavailable' },
      ],
    });
    if (value === 'unavailable') notifyAdUnavailable();
    return value === 'ok';
  }

  async showInterstitial(): Promise<void> {
    await dialog({
      title: 'テスト広告（合間）',
      body: '本番ではここに全画面の広告が出ます。買い切りにすると出なくなります。',
      buttons: [{ label: '閉じる', value: 'ok', primary: true }],
      autoCloseMs: 2500,
      autoValue: 'ok',
    });
  }

  async purchase(productId: ProductId): Promise<PurchaseOutcome> {
    const product = PRODUCTS[productId];
    const value = await dialog({
      title: `テスト購入：${product.title}`,
      body: `${product.fallbackPrice}（テストなのでお金は動きません）`,
      buttons: [
        { label: '購入する', value: 'purchased', primary: true },
        { label: 'やめる', value: 'cancelled' },
        // ショップの「購入できませんでした」の表示を試す用
        { label: '失敗にする', value: 'failed' },
      ],
    });
    return value as PurchaseOutcome;
  }

  async restorePurchases(): Promise<ProductId[]> {
    return [];
  }

  priceOf(productId: ProductId): string | null {
    return this.pricesReady ? PRODUCTS[productId].fallbackPrice : null;
  }

  private priceTask: Promise<boolean> | null = null;

  refreshPrices(): Promise<boolean> {
    if (!this.canPurchase) return Promise.resolve(false);
    if (this.pricesReady) return Promise.resolve(true);
    this.priceTask ??= new Promise<boolean>((resolve) => {
      setTimeout(() => {
        if (this.priceFailures > 0) this.priceFailures -= 1;
        else this.pricesReady = true;
        this.priceTask = null;
        resolve(this.pricesReady);
      }, this.priceDelayMs);
    });
    return this.priceTask;
  }

  /* レビュー依頼・お知らせはブラウザでは何もしない（実機の TestFlight で確かめる） */
  async requestReview(): Promise<void> {}
  readonly canNotify = false;
  async requestNotificationPermission(): Promise<boolean> {
    return false;
  }
  async scheduleNotices(): Promise<void> {}
  async cancelNotices(): Promise<void> {}
  onNoticeOpened(): void {}
}
